import Phaser from 'phaser';
import type { MapData } from '@/types/map-data';
import { tileToPixelX, tileToPixelY } from '@/maps/map-renderer';
import { FRUIT_SCORES } from '@/config/scoring';
import { TIMING } from '@/config/speed';

const FRUIT_RADIUS = 6;

interface FruitConfig {
  readonly name: string;
  readonly score: number;
  readonly color: number;
}

const FRUIT_COLORS: Record<string, number> = {
  Cherry: 0xff0000,
  Strawberry: 0xff3366,
  Orange: 0xff8800,
  Apple: 0x00cc00,
  Melon: 0x00ff88,
  Galaxian: 0x0088ff,
  Bell: 0xffdd00,
  Key: 0xcccccc,
} as const;

/** Dot-count thresholds that trigger fruit spawning (twice per level). */
const FRUIT_SPAWN_THRESHOLDS: readonly [number, number] = [70, 170];

export class FruitEntity {
  private readonly scene: Phaser.Scene;
  private readonly pixelX: number;
  private readonly pixelY: number;
  private readonly tileX: number;
  private readonly tileY: number;

  private graphics: Phaser.GameObjects.Graphics | null = null;
  private labelText: Phaser.GameObjects.Text | null = null;
  private visible = false;
  private collected = false;
  private despawnTimer = 0;
  private spawnCount = 0;
  private currentFruit: FruitConfig | null = null;
  private bounceTween: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene, mapData: MapData) {
    this.scene = scene;
    this.tileX = mapData.fruitSpawn.x;
    this.tileY = mapData.fruitSpawn.y;
    this.pixelX = tileToPixelX(this.tileX);
    this.pixelY = tileToPixelY(this.tileY);
    this.graphics = scene.add.graphics();
    this.graphics.setPosition(this.pixelX, this.pixelY);
    this.graphics.setVisible(false);
  }

  /** Check if fruit should spawn based on dots eaten count. */
  checkSpawn(dotsEaten: number, level: number): void {
    if (this.visible || this.spawnCount >= FRUIT_SPAWN_THRESHOLDS.length) {
      return;
    }

    const threshold = FRUIT_SPAWN_THRESHOLDS[this.spawnCount];
    if (threshold === undefined || dotsEaten < threshold) {
      return;
    }

    this.spawn(level);
  }

  /** Spawn a fruit at the designated position. */
  private spawn(level: number): void {
    const fruitIndex = Math.min(level - 1, FRUIT_SCORES.length - 1);
    const fruitData = FRUIT_SCORES[fruitIndex];
    if (!fruitData) {
      return;
    }

    const color = FRUIT_COLORS[fruitData.name] ?? 0xffffff;
    this.currentFruit = {
      name: fruitData.name,
      score: fruitData.score,
      color,
    };

    this.visible = true;
    this.collected = false;
    this.despawnTimer = 0;
    this.spawnCount++;

    this.draw();

    if (this.graphics) {
      this.graphics.setVisible(true);
    }

    // Add a subtle bounce animation
    if (this.graphics) {
      this.bounceTween = this.scene.tweens.add({
        targets: this.graphics,
        scaleX: { from: 0.8, to: 1.1 },
        scaleY: { from: 0.8, to: 1.1 },
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /** Update fruit timer; despawn if time runs out. */
  update(delta: number): void {
    if (!this.visible) {
      return;
    }

    this.despawnTimer += delta;

    if (this.despawnTimer >= TIMING.FRUIT_DISPLAY_DURATION_MS) {
      this.despawn();
    }
  }

  /** Check if Pac-Man collected the fruit. Returns score if collected, 0 otherwise. */
  checkCollection(pacTileX: number, pacTileY: number): number {
    if (!this.visible || this.collected || !this.currentFruit) {
      return 0;
    }

    if (pacTileX === this.tileX && pacTileY === this.tileY) {
      this.collected = true;
      this.despawn();
      return this.currentFruit.score;
    }

    return 0;
  }

  /** Get the current fruit name (for display purposes). */
  getCurrentFruitName(): string {
    return this.currentFruit?.name ?? '';
  }

  isVisible(): boolean {
    return this.visible;
  }

  getSpawnCount(): number {
    return this.spawnCount;
  }

  getPixelX(): number {
    return this.pixelX;
  }

  getPixelY(): number {
    return this.pixelY;
  }

  private despawn(): void {
    this.visible = false;

    if (this.graphics) {
      this.graphics.setVisible(false);
      this.graphics.clear();
    }

    if (this.bounceTween) {
      this.bounceTween.destroy();
      this.bounceTween = null;
    }

    if (this.labelText) {
      this.labelText.destroy();
      this.labelText = null;
    }
  }

  private draw(): void {
    if (!this.graphics || !this.currentFruit) {
      return;
    }

    this.graphics.clear();

    const color = this.currentFruit.color;

    // Draw at local origin (0,0) since graphics is positioned at (pixelX, pixelY).
    // This ensures the scale tween pulses in-place instead of drifting.
    this.graphics.fillStyle(color, 1);
    this.graphics.fillCircle(0, 0, FRUIT_RADIUS);

    // Draw a highlight dot for 3D effect
    this.graphics.fillStyle(0xffffff, 0.5);
    this.graphics.fillCircle(-2, -2, 2);

    // Draw stem for cherry/strawberry/apple
    const name = this.currentFruit.name;
    if (name === 'Cherry' || name === 'Strawberry' || name === 'Apple') {
      this.graphics.lineStyle(2, 0x006600, 1);
      this.graphics.beginPath();
      this.graphics.moveTo(0, -FRUIT_RADIUS);
      this.graphics.lineTo(2, -FRUIT_RADIUS - 4);
      this.graphics.strokePath();
    }
  }

  destroy(): void {
    this.despawn();
    if (this.graphics) {
      this.graphics.destroy();
      this.graphics = null;
    }
  }
}

export { FRUIT_SPAWN_THRESHOLDS };
